import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FeaturedCarousel from '../components/training/FeaturedCarousel';
import VideoRow from '../components/training/VideoRow';
import SafeIcon from '../components/common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { TRAINING_ROW_SLUGS } from '../constants/training';
import {
  fetchContinueWatchingSessions,
  fetchPathsWithSessions,
  fetchSessions,
  checkCanViewTrainingArchive,
  checkIsAdminOrHigher,
} from '../services/training';

const HERO_SLIDE_COUNT = 5;

export default function TrainingLibraryPage() {
  const navigate = useNavigate();
  const { user, canManageTraining } = useAuth();
  const [categories, setCategories] = useState([]);
  const [featuredSessions, setFeaturedSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchiveLink, setShowArchiveLink] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const [paths, continueWatching, allSessions, canViewArchive, admin] = await Promise.all([
          fetchPathsWithSessions(),
          user?.id ? fetchContinueWatchingSessions(user.id) : Promise.resolve([]),
          fetchSessions(),
          checkCanViewTrainingArchive().catch(() => false),
          checkIsAdminOrHigher().catch(() => false),
        ]);

        if (!active) return;
        setShowArchiveLink(canViewArchive);
        setIsAdmin(admin);

        const rows = [];
        if (continueWatching.length > 0) {
          rows.push({
            id: TRAINING_ROW_SLUGS.CONTINUE,
            name: 'Continuar Viendo',
            sessions: continueWatching,
          });
        }

        if (allSessions.length > 0) {
          rows.push({
            id: TRAINING_ROW_SLUGS.ALL_SESSIONS,
            name: 'Todos los Videos',
            sessions: allSessions,
          });
        }

        rows.push(...paths);

        setCategories(rows);

        const heroSlides = allSessions.slice(0, HERO_SLIDE_COUNT);
        if (heroSlides.length > 0) {
          setFeaturedSessions(heroSlides);
        } else {
          const fallback = paths.find((p) => p.sessions.length > 0)?.sessions?.[0];
          setFeaturedSessions(fallback ? [fallback] : []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => { active = false; };
  }, [user?.id]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-[var(--text-secondary)] text-sm font-bold uppercase tracking-widest">
        Cargando entrenamientos…
      </div>
    );
  }

  if (featuredSessions.length === 0 && categories.length === 0) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 px-8 text-center">
        <p className="text-xl font-black text-[var(--text-main)] uppercase tracking-tight">
          Biblioteca de entrenamientos
        </p>
        <p className="text-[var(--text-secondary)] max-w-md">
          Aún no hay sesiones publicadas. Los instructores agregarán contenido pronto.
        </p>
      </div>
    );
  }

  return (
    <div className="pb-24 bg-[var(--bg-base)] transition-colors duration-300 relative">
      {(showArchiveLink || canManageTraining || isAdmin) && (
        <div className="absolute top-4 right-4 md:top-6 md:right-8 z-30 flex flex-wrap items-center justify-end gap-2">
          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate('/app/entrenamientos/borradores-antiguos')}
              className="flex items-center gap-2 bg-[var(--bg-surface)] border border-[var(--border-subtle)] text-[var(--text-main)] px-5 py-3 rounded-full font-black text-[10px] uppercase tracking-widest shadow-lg hover:scale-105 transition-transform"
            >
              <SafeIcon name="Clock" className="w-4 h-4" />
              Borradores
            </button>
          )}
          {showArchiveLink && (
            <button
              type="button"
              onClick={() => navigate('/app/entrenamientos/archivo')}
              className="flex items-center gap-2 bg-[var(--bg-surface)] border border-[var(--border-subtle)] text-[var(--text-main)] px-5 py-3 rounded-full font-black text-[10px] uppercase tracking-widest shadow-lg hover:scale-105 transition-transform"
            >
              <SafeIcon name="Archive" className="w-4 h-4" />
              Archivo
            </button>
          )}
          {canManageTraining && (
            <button
              type="button"
              onClick={() => navigate('/app/entrenamientos/subir')}
              className="flex items-center gap-2 gfi-gradient-bg text-brand-navy px-5 py-3 rounded-full font-black text-[10px] uppercase tracking-widest shadow-lg hover:scale-105 transition-transform"
            >
              <SafeIcon name="Upload" className="w-4 h-4" />
              Subir sesión
            </button>
          )}
        </div>
      )}

      <FeaturedCarousel sessions={featuredSessions} />

      <div className="space-y-16">
        {categories.map((category) => (
          <div key={category.id} className="relative">
            <VideoRow
              title={category.name}
              sessions={category.sessions}
              isComingSoon={category.isComingSoon}
              rowId={category.id}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
